// src/store/slices/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OrgMembership, ProjectRole } from '@app-types/access';

export interface IProject {
  projectId?: string;
  _id?: string;
  name: string;
  slug: string;
  orgId?: string;
  status?: 'active' | 'deleting';
  role?: ProjectRole;
  createdAt?: string;
  createdBy?: string;
  description?: string;
  hasProductionChannel?: boolean;
}

export interface IOrganization {
  _id?: string;
  orgId: string;
  name: string;
  slug: string;
  description?: string;
  status?: 'active' | 'deleting';
  createdAt?: string;
}

export interface IUser {
  email: string;
  firstName: string;
  lastName: string;
  isActivated?: boolean;
  projects?: IProject[];
  organizations?: IOrganization[];
  memberships?: OrgMembership[];
}

interface AuthState {
  token: string | null;
  user: IUser | null;
}

const initialState: AuthState = {
  token: null,
  user: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ token: string; user: AuthState['user'] }>) => {
      const rawUser = action.payload.user as (AuthState['user'] & { activated?: boolean }) | null;
      const normalizedUser = rawUser
        ? (() => {
            const { activated, ...rest } = rawUser;
            return {
              ...rest,
              isActivated: rest.isActivated ?? activated,
            };
          })()
        : null;
      state.token = action.payload.token;
      state.user = normalizedUser;
    },
    addOrganization: (
      state,
      action: PayloadAction<{ organization: IOrganization; membership: OrgMembership }>,
    ) => {
      if (!state.user) return;
      const { organization, membership } = action.payload;
      const orgs = state.user.organizations ?? [];
      if (!orgs.some(o => o.orgId === organization.orgId)) {
        state.user.organizations = [...orgs, organization];
      }
      const memberships = state.user.memberships ?? [];
      if (!memberships.some(m => m.orgId === membership.orgId)) {
        state.user.memberships = [...memberships, membership];
      }
    },
    logout: state => {
      state.token = null;
      state.user = null;
    },
  },
});

export const { setCredentials, addOrganization, logout } = authSlice.actions;
export default authSlice.reducer;
